import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Send } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useActiveMatter } from '../../lib/activeMatter';
import { ClientAnnexEditor } from './ClientAnnexEditor';
import { errMsg } from '../../lib/errors';

const ST = { pending: ['Esperando visto bueno', 'text-slate-300'], approved: ['Aprobado por el cliente', 'text-emerald-300'], changes: ['El cliente pidió cambios', 'text-amber-300'] };

// Cambios con Fidem: status of the automatic revision and a retry if it failed
const RevisionStatus = ({ matter, a, onDone }) => {
  const { api } = useAuth();
  const [, setActive] = useActiveMatter();
  const navigate = useNavigate();
  const retry = async () => {
    try { await api.post(`/crm/matters/${matter.id}/brief/approval/revise`); toast.success('Fidem está corrigiendo el escrito (2-5 min)'); onDone(); } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo'); }
  };
  if (a.revision_status === 'running') return <span data-testid="brief-revision-running" className="flex items-center gap-1 text-violet-300"><Loader2 className="h-3 w-3 animate-spin" />Fidem está aplicando los cambios...</span>;
  if (a.revision_status === 'ready') return <button data-testid="brief-revision-ready" onClick={() => { setActive(matter); navigate('/fidem'); }} className="rounded-full border border-violet-500/60 px-3 py-1 text-violet-200">Fidem corrigió el escrito · confirmar reenvío en Fidem</button>;
  return <button data-testid="brief-revision-retry" onClick={retry} className="rounded-full border border-violet-500/60 px-3 py-1 text-violet-200">{a.revision_status === 'failed' ? 'Reintentar corrección con Fidem' : 'Que Fidem aplique los cambios'}</button>;
};

// Fidem Presenta from the brief dialog: send to the client for approval and see the answer
export const BriefApproval = ({ matter, deadline }) => {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [annex, setAnnex] = useState(null);
  const load = () => api.get(`/crm/matters/${matter.id}/brief/approvals`, { params: deadline ? { deadline_id: deadline.id } : {} }).then((r) => setRows(r.data.approvals)).catch(() => {});
  useEffect(() => { load(); api.get(`/case-strategy/${matter.id}/client-annex/latest`).then((r) => setAnnex(r.data.annex)).catch(() => {}); }, [matter.id, deadline?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/crm/matters/${matter.id}/brief/approval`, { deadline_id: deadline?.id || null, annex_id: annex?.id || null });
      toast.success(`Enviado a ${r.data.sent_to} para su visto bueno${annex ? ' con el informe estratégico aparte' : ''}`); load();
    } catch (e) { toast.error(errMsg(e, 'No se pudo enviar')); } finally { setBusy(false); }
  };
  const last = rows[0];
  return (
    <div data-testid="brief-approval" className="flex flex-wrap items-center gap-2 text-xs">
      <ClientAnnexEditor matter={matter} annex={annex} onAnnex={setAnnex} />
      <button data-testid="brief-approval-btn" disabled={busy} onClick={send} className="flex items-center gap-1 rounded-full bg-violet-600 px-3 py-1.5 text-white disabled:opacity-50">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}Pedir visto bueno al cliente
      </button>
      {last && (
        <span data-testid="brief-approval-status" className={ST[last.status]?.[1]}>
          {ST[last.status]?.[0]} · {last.client_name} · {new Date(last.answered_at || last.sent_at).toLocaleString('es-CL')}{last.comment ? ` · «${last.comment}»` : ''}{last.reminded_at && last.status === 'pending' ? ' · recordatorio enviado' : ''}
        </span>
      )}
      {last?.status === 'changes' && <RevisionStatus matter={matter} a={last} onDone={load} />}
      {last?.status === 'approved' && last.signer_name && (
        <button data-testid="brief-signed-pdf" onClick={async () => {
          try {
            const r = await api.get(`/crm/matters/${matter.id}/brief/approval/signed.pdf`, { params: deadline ? { deadline_id: deadline.id } : {}, responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
            const el = document.createElement('a'); el.href = url; el.download = `escrito-firmado-${matter.id.slice(0, 8)}.pdf`; el.click(); URL.revokeObjectURL(url);
          } catch { toast.error('No se pudo descargar'); }
        }} className="rounded-full bg-emerald-700 px-3 py-1 text-white">PDF firmado por {last.signer_name}</button>
      )}
    </div>
  );
};
